import { createAsyncThunk, createEntityAdapter, createSlice, EntityState } from "@reduxjs/toolkit";
import { AppDataState, AppId, AppThunkAPIType, unknownError } from "../../../../app/appTypes";
import { RootState } from "../../../../app/store";
import { deleteWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../../../http"

export interface SysMailTemplate {
    id: AppId,
    type: string,
    context: string,
    body: string,
    bodyEN: string,
    subject: string,
    subjectEN: string,
    isFixed: boolean;
    isInternal: boolean;
}

export type DraftSysMailTemplate = Omit<SysMailTemplate, "id">;

const adapter = createEntityAdapter<SysMailTemplate>({
    selectId: (mailFooter) => mailFooter.id,
})

export type SysMailTemplatesState = EntityState<SysMailTemplate> & { state: AppDataState }

const initialState: SysMailTemplatesState = adapter.getInitialState({
    state: { type: "empty" },
})

export const loadSysMailTemplates = createAsyncThunk<SysMailTemplate[], void, AppThunkAPIType>("sysMailTemplates/load", async (context, { getState, requestId, dispatch, rejectWithValue }) => {
    const result = await dispatch(getWithAuth({ url: `api/sys/mail-templates` }))
    const { payload } = result

    if (getWithAuth.fulfilled.match(result)) {
        return payload as SysMailTemplate[]
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }

})

export const createSysMailTemplate = createAsyncThunk<SysMailTemplate, DraftSysMailTemplate, AppThunkAPIType>(
    "sysMailTemplates/new",
    async (mailTemplate: DraftSysMailTemplate, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({ url: "api/sys/mail-templates", payload: mailTemplate }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as SysMailTemplate
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateSysMailTemplate = createAsyncThunk<SysMailTemplate, SysMailTemplate, AppThunkAPIType>(
    "sysMailTemplates/edit",
    async (mailTemplate: SysMailTemplate, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({ url: `api/sys/mail-templates/${mailTemplate.id}`, payload: mailTemplate }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return mailTemplate
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const deleteSysMailTemplate = createAsyncThunk<string, string, AppThunkAPIType>(
    "sysMailTemplates/delete",
    async (mailTemplateId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(deleteWithAuth({ url: `api/sys/mail-templates/${mailTemplateId}` }))
        const { payload } = result
        if (deleteWithAuth.fulfilled.match(result)) {
            return mailTemplateId
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const sysMailTemplatesSlice = createSlice({
    name: "sysMailTemplates",
    initialState,
    reducers: {
        clearRequirementsTypesSlice: (state) => {
            adapter.removeAll(state)
            state.state = { type: "empty" }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(createSysMailTemplate.fulfilled, (state, action) => {
            state = adapter.addOne(state, action.payload)
        });

        builder.addCase(updateSysMailTemplate.fulfilled, (state, action) => {
            const { id } = action.payload
            const changes: Omit<SysMailTemplate, "id"> = action.payload
            state = adapter.updateOne(state, {
                id,
                changes,
            });
        });

        builder.addCase(loadSysMailTemplates.rejected, (state, action) => {
            if (state.state.type === "loading" && state.state.requestId === action.meta.requestId) {
                state.state = {
                    type: "error",
                    error: action.payload ?? unknownError(),
                }
            }
        })

        builder.addCase(loadSysMailTemplates.pending, (state, action) => {
            if (state.state.type === "empty" || state.state.type === "loaded") {
                state.state = {
                    type: "loading",
                    requestId: action.meta.requestId,
                }
            }
        })


        builder.addCase(loadSysMailTemplates.fulfilled, (state, action) => {
            if (state.state.type === "loading" && state.state.requestId === action.meta.requestId) {
                adapter.setAll(state, action.payload)
                state.state = { type: "loaded" }
            }
        })

        builder.addCase(deleteSysMailTemplate.fulfilled, (state, action) => {
            state = adapter.removeOne(state, action.payload)
        })
    },
})

export const selectSysMailTemplates = (state: RootState): SysMailTemplatesState => state.sysMailTemplates

export const
    { selectAll: selectAllSysMailTemplates
        , selectById: selectSysMailTemplateById
        , selectTotal: selectTotalSysMailTemplates
        , selectEntities: selectSysMailTemplatesEntities
    } = adapter.getSelectors<RootState>(selectSysMailTemplates)

export default sysMailTemplatesSlice.reducer;
